import React from 'react';

/**
 * "Add Images" block on the Board Editor — collapsible drop zone, with a
 * second sub-panel for pasting external image URLs (Pinterest / Unsplash /
 * direct links). Pure presentational; no state of its own.
 */
const BoardAddImagesPanel = ({
  t,
  isAddImagesOpen,
  setIsAddImagesOpen,

  // Dropzone
  getRootProps,
  getInputProps,
  isDragActive,
  uploading,

  // Quick actions row
  setShowLibraryModal,
  saveToLibrary,
  setSaveToLibrary,

  // External URL panel
  showExternalUrlInput,
  setShowExternalUrlInput,
  externalUrlText,
  parsedUrls,
  addingExternal,
  addProgress,
  externalSaveToLibrary,
  setExternalSaveToLibrary,
  handleExternalUrlTextChange,
  handleAddExternalUrls,
  resetExternalUrlInput,
}) => {
  return (
    <>
      <div style={{ marginBottom: '40px', marginTop: '8px' }} data-testid="add-images-section">
        {!isAddImagesOpen ? (
          <button
            type="button"
            data-testid="add-images-toggle"
            onClick={() => setIsAddImagesOpen(true)}
            className="add-images-summary"
          >
            <span className="add-images-summary__title">{t.add_images}</span>
            <span className="add-images-summary__subtitle">{t.add_images_subtitle}</span>
            <span className="add-images-summary__chevron" aria-hidden="true">›</span>
          </button>
        ) : (
          <>
            {/* Soft header above dropzone with a discreet collapse affordance */}
            <div className="add-images-header">
              <span className="add-images-header__title">{t.add_images}</span>
              <button
                type="button"
                data-testid="add-images-collapse"
                onClick={() => {
                  setIsAddImagesOpen(false);
                  if (showExternalUrlInput) resetExternalUrlInput();
                }}
                className="add-images-header__close"
                aria-label="Collapse Add Images"
              >
                ✕
              </button>
            </div>
            {/* Upload zone — soft beige, dashed border, max-width 520px
                centered. Reduced from 600px / 72px padding to a more
                modest 520px / 48px so it doesn't dominate the
                editorial space inside Shared Client Space. */}
            <div
              {...getRootProps()}
              data-testid="upload-zone"
              style={{
                maxWidth: '520px',
                margin: '0 auto',
                backgroundColor: isDragActive ? 'var(--kahla-cream)' : '#F8F6F2',
                padding: '48px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'background-color 0.25s ease, border-color 0.25s ease',
                border: `1px dashed ${isDragActive ? 'var(--kahla-charcoal)' : 'rgba(166,152,136,0.4)'}`,
                outline: 'none',
              }}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                  <div className="spinner" style={{ width: '22px', height: '22px' }}></div>
                  <span style={{ fontSize: '13px', color: 'var(--kahla-text-light)' }}>Uploading...</span>
                </div>
              ) : (
                <>
                  <h3 style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '18px',
                    fontWeight: 400,
                    color: 'var(--kahla-charcoal)',
                    margin: 0,
                    letterSpacing: '0.01em',
                  }}>
                    {isDragActive ? t.drop_to_upload : t.add_images}
                  </h3>
                  <p style={{
                    fontSize: '12px',
                    color: 'var(--kahla-text-light)',
                    marginTop: '8px',
                    letterSpacing: '0.04em',
                  }}>
                    {t.drag_drop_or_click}
                  </p>
                </>
              )}
            </div>

            {/* Subtle action links + save-to-library checkbox */}
            <div style={{
              marginTop: '14px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px 16px',
              fontSize: '12px',
              color: 'var(--kahla-text-light)',
            }}>
              <button
                type="button"
                data-testid="choose-from-library-btn"
                onClick={() => setShowLibraryModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  fontSize: '12px',
                  color: 'var(--kahla-taupe)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '0.04em',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(166,152,136,0.35)',
                  textUnderlineOffset: '3px',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
              >
                {t.from_library}
              </button>
              <span style={{ opacity: 0.4, userSelect: 'none' }}>·</span>
              <button
                type="button"
                data-testid="add-external-url-btn"
                onClick={() => setShowExternalUrlInput(!showExternalUrlInput)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px 0',
                  fontSize: '12px',
                  color: showExternalUrlInput ? 'var(--kahla-charcoal)' : 'var(--kahla-taupe)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '0.04em',
                  textDecoration: 'underline',
                  textDecorationColor: 'rgba(166,152,136,0.35)',
                  textUnderlineOffset: '3px',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = showExternalUrlInput ? 'var(--kahla-charcoal)' : 'var(--kahla-taupe)';
                }}
              >
                {t.add_via_link}
              </button>
              <span style={{ opacity: 0.4, userSelect: 'none' }}>·</span>
              <label
                data-testid="save-to-library-label"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  color: 'var(--kahla-text-light)',
                  fontSize: '12px',
                  letterSpacing: '0.02em',
                }}
              >
                <input
                  type="checkbox"
                  data-testid="save-to-library-checkbox"
                  checked={saveToLibrary}
                  onChange={(e) => setSaveToLibrary(e.target.checked)}
                  style={{
                    width: '13px',
                    height: '13px',
                    accentColor: 'var(--kahla-taupe)',
                    cursor: 'pointer',
                  }}
                />
                {t.also_save_uploads}
              </label>
            </div>
          </>
        )}
      </div>

      {/* External URL Input Panel */}
      {isAddImagesOpen && showExternalUrlInput && (
        <div
          style={{
            padding: '20px',
            marginBottom: '16px',
            backgroundColor: 'var(--kahla-cream-light)',
            border: '1px solid var(--kahla-border)',
          }}
          data-testid="external-url-panel"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px' }}>{t.add_external_image_urls}</label>
            <button
              onClick={resetExternalUrlInput}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--kahla-text-light)' }}
              data-testid="close-external-url-panel"
            >
              ✕
            </button>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', marginBottom: '12px' }}>
            Paste one or more image URLs (one per line). Supports Pinterest, Unsplash, and other direct image links.
          </p>
          <textarea
            value={externalUrlText}
            onChange={handleExternalUrlTextChange}
            placeholder={"https://i.pinimg.com/.../image1.jpg\nhttps://i.pinimg.com/.../image2.jpg\nhttps://images.unsplash.com/.../photo.png"}
            data-testid="external-url-input"
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid var(--kahla-border)',
              backgroundColor: 'var(--kahla-white)',
              fontSize: '13px',
              marginBottom: '12px',
              resize: 'vertical',
              fontFamily: 'monospace',
              lineHeight: '1.5'
            }}
          />

          {/* URL Status Summary */}
          {parsedUrls.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: 'var(--kahla-white)',
                border: '1px solid var(--kahla-border)',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '12px', color: 'var(--kahla-text)' }}>
                  {parsedUrls.filter(u => u.valid).length} valid URL{parsedUrls.filter(u => u.valid).length !== 1 ? 's' : ''}
                  {parsedUrls.filter(u => !u.valid).length > 0 && (
                    <span style={{ color: '#c75050' }}>
                      {' '}· {parsedUrls.filter(u => !u.valid).length} invalid
                    </span>
                  )}
                </span>
              </div>

              {/* URL Previews Grid */}
              {parsedUrls.filter(u => u.valid).length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '8px' }}>
                    Preview ({parsedUrls.filter(u => u.valid).length} image{parsedUrls.filter(u => u.valid).length !== 1 ? 's' : ''}):
                  </p>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    padding: '4px'
                  }}>
                    {parsedUrls.filter(u => u.valid).slice(0, 12).map((urlObj, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: '70px',
                          height: '70px',
                          border: '1px solid var(--kahla-border)',
                          overflow: 'hidden',
                          backgroundColor: 'var(--kahla-white)',
                          position: 'relative'
                        }}
                      >
                        <img
                          src={urlObj.url}
                          alt={`Preview ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:9px;color:#c75050;text-align:center;padding:4px;">Load error</div>';
                          }}
                        />
                      </div>
                    ))}
                    {parsedUrls.filter(u => u.valid).length > 12 && (
                      <div style={{
                        width: '70px',
                        height: '70px',
                        border: '1px solid var(--kahla-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--kahla-cream)',
                        fontSize: '12px',
                        color: 'var(--kahla-text-light)'
                      }}>
                        +{parsedUrls.filter(u => u.valid).length - 12}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invalid URLs Warning */}
              {parsedUrls.filter(u => !u.valid).length > 0 && (
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #ffcccc',
                  marginBottom: '12px'
                }}>
                  <p style={{ fontSize: '11px', color: '#c75050', marginBottom: '4px', fontWeight: '500' }}>
                    Invalid URLs (will be skipped):
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '16px' }}>
                    {parsedUrls.filter(u => !u.valid).slice(0, 3).map((urlObj, idx) => (
                      <li key={idx} style={{ fontSize: '10px', color: '#c75050', wordBreak: 'break-all' }}>
                        {urlObj.url.length > 50 ? urlObj.url.substring(0, 50) + '...' : urlObj.url}
                      </li>
                    ))}
                    {parsedUrls.filter(u => !u.valid).length > 3 && (
                      <li style={{ fontSize: '10px', color: '#c75050' }}>
                        ...and {parsedUrls.filter(u => !u.valid).length - 3} more
                      </li>
                    )}
                  </ul>
                  <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)', marginTop: '6px' }}>
                    URLs must end with .jpg, .jpeg, .png, .webp, or .gif
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Save to Library Option */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px',
            padding: '10px 12px',
            backgroundColor: 'var(--kahla-white)',
            border: '1px solid var(--kahla-border)'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              fontSize: '13px',
              color: 'var(--kahla-text)'
            }}>
              <input
                type="checkbox"
                checked={externalSaveToLibrary}
                onChange={(e) => setExternalSaveToLibrary(e.target.checked)}
                data-testid="external-save-to-library"
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: 'var(--kahla-charcoal)'
                }}
              />
              Also save to Library
            </label>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
              (reusable across moodboards)
            </span>
          </div>

          {/* Progress indicator */}
          {addingExternal && addProgress.total > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                height: '4px',
                backgroundColor: 'var(--kahla-border)',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(addProgress.current / addProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: 'var(--kahla-charcoal)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '4px', textAlign: 'center' }}>
                Adding {addProgress.current} of {addProgress.total}...
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn-secondary"
              onClick={resetExternalUrlInput}
              disabled={addingExternal}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleAddExternalUrls}
              disabled={parsedUrls.filter(u => u.valid).length === 0 || addingExternal}
              data-testid="add-external-url-submit"
            >
              {addingExternal
                ? `Adding ${addProgress.current}/${addProgress.total}...`
                : `Add ${parsedUrls.filter(u => u.valid).length || ''} Image${parsedUrls.filter(u => u.valid).length !== 1 ? 's' : ''} to Board`}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BoardAddImagesPanel;
